import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'theme';

function getSystemTheme() {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function readStored() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function applyClass(theme) {
  const root = document.documentElement;
  if (theme === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
}

export function useTheme() {
  const [stored, setStored] = useState(() => readStored()); // 'light' | 'dark' | null
  const [systemTheme, setSystemTheme] = useState(getSystemTheme);

  const source = stored ? 'user' : 'system';
  const theme = stored || systemTheme;

  // Слушаем изменение системной темы (важно когда source === 'system').
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemTheme(e.matches ? 'dark' : 'light');
    if (mql.addEventListener) mql.addEventListener('change', handler);
    else mql.addListener(handler);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler);
      else mql.removeListener(handler);
    };
  }, []);

  // Применяем класс при любом изменении эффективной темы.
  useEffect(() => {
    applyClass(theme);
  }, [theme]);

  const setLight = useCallback(() => {
    try { localStorage.setItem(STORAGE_KEY, 'light'); } catch {}
    setStored('light');
  }, []);

  const setDark = useCallback(() => {
    try { localStorage.setItem(STORAGE_KEY, 'dark'); } catch {}
    setStored('dark');
  }, []);

  const setAuto = useCallback(() => {
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    setStored(null);
  }, []);

  const toggle = useCallback(() => {
    const next = theme === 'dark' ? 'light' : 'dark';
    try { localStorage.setItem(STORAGE_KEY, next); } catch {}
    setStored(next);
  }, [theme]);

  return { theme, source, setLight, setDark, setAuto, toggle };
}
